<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

const props = withDefaults(
	defineProps<{
		closableOnBackdrop?: boolean;
		closableOnEsc?: boolean;
	}>(),
	{
		closableOnBackdrop: true,
		closableOnEsc: true,
	},
);

const emit = defineEmits<{
	close: [];
}>();

const modalRef = ref<HTMLDivElement | null>(null);
const previouslyFocused = ref<Element | null>(null);

function emitClose() {
	emit('close');
}

function handleBackdropClick() {
	if (props.closableOnBackdrop) {
		emitClose();
	}
}

function handleEsc(event: KeyboardEvent) {
	if (event.key !== 'Escape' || !props.closableOnEsc) {
		return;
	}

	event.preventDefault();
	emitClose();
}

onMounted(() => {
	previouslyFocused.value = document.activeElement;
	document.addEventListener('keydown', handleEsc);
	modalRef.value?.focus();
});

onUnmounted(() => {
	document.removeEventListener('keydown', handleEsc);

	if (previouslyFocused.value instanceof HTMLElement) {
		previouslyFocused.value.focus();
	}
});
</script>

<template>
	<div class="fixed inset-0 z-[1000] flex items-center justify-center p-4" aria-live="polite">
		<div class="absolute inset-0 bg-black/70" @click="handleBackdropClick" />

		<div
			ref="modalRef"
			class="relative z-1 w-full sm:max-w-140 rounded-2xl border border-cardBorder bg-mainBg p-4 sm:p-6 text-primaryText shadow-2xl"
			role="dialog"
			aria-modal="true"
			tabindex="-1"
		>
			<slot />
		</div>
	</div>
</template>
